import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Pagination, Popover, Typography, keyframes } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { AlertStatsCard, AlertsTable } from "./components";
import socket from "../../utils/socket";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SystemAlertsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("All");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const datePickerOpen = Boolean(dateAnchorEl);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  // Fetch leads via socket (like Dashboard)
  const fetchLeads = useCallback(() => {
    if (fetchedRef.current) return;

    fetchedRef.current = true;
    setLoading(true);
    setLeads([]);

    socket.off("leads-batch");
    socket.off("leads-complete");
    socket.off("leads-error");

    const emitFetch = () => {
      socket.emit("get-leads-batch", { batchSize: 100 });
    };

    if (socket.connected) {
      emitFetch();
    } else {
      socket.connect();
      socket.once("connect", emitFetch);
    }

    socket.on("leads-batch", (batch) => {
      setLeads((prev) => {
        const next = [...prev, ...batch];
        if (next.length > 0) setLoading(false);
        return next;
      });
    });

    socket.once("leads-complete", () => {
      setLoading(false);
      fetchedRef.current = false;
      socket.disconnect();
    });

    socket.once("leads-error", () => {
      setLoading(false);
      fetchedRef.current = false;
      socket.disconnect();
    });
  }, []);

  useEffect(() => {
    fetchLeads();

    return () => {
      socket.off("leads-batch");
      socket.off("leads-complete");
      socket.off("leads-error");
      socket.disconnect();
      fetchedRef.current = false;
    };
  }, []);

  // Compute alerts from leads
  const { alertStats, alerts } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);

    let stuckLeads = 0;
    let slaBreaches = 0;
    let idleAccounts = 0;

    const alertList = [];

    leads.forEach((lead) => {
      const lastUpdate = new Date(lead.updatedAt || lead.createdAt);
      const createdDate = new Date(lead.createdAt);
      const daysSinceCreated = Math.floor(
        (now - createdDate) / (1000 * 60 * 60 * 24),
      );
      const daysSinceUpdate = Math.floor(
        (today - lastUpdate) / (1000 * 60 * 60 * 24),
      );
      const status = lead.current_status?.key?.toLowerCase() || "";

      // Stuck leads - no activity for 3+ days
      if (lastUpdate < threeDaysAgo) {
        stuckLeads++;
        const assignee = lead.assign_employee;
        let agentName = "Unassigned";
        if (assignee) {
          agentName =
            typeof assignee === "object"
              ? assignee.name || assignee.label || "Unassigned"
              : "Unassigned";
        }

        alertList.push({
          id: lead._id,
          severity: "warning",
          severityIcon: WarningAmberIcon,
          severityColor: "#F59E0B",
          alertType: `Lead Stuck ${daysSinceUpdate} Days`,
          subject: lead.client_name || lead.name || "Unknown",
          stage: lead.current_status?.key || "Unknown",
          stageBg: "#F1F5F9",
          stageColor: "#475569",
          timeIdle: `${daysSinceUpdate} days`,
          timeIdleColor: "#1E293B",
          assignedAgent: agentName,
        });
      }

      // SLA breaches - new leads untouched for 2+ days
      if (
        daysSinceCreated >= 2 &&
        ["new", "untouched", "inquiry"].includes(status)
      ) {
        slaBreaches++;
        const assignee = lead.assign_employee;
        let agentName = "Unassigned";
        if (assignee) {
          agentName =
            typeof assignee === "object"
              ? assignee.name || assignee.label || "Unassigned"
              : "Unassigned";
        }

        alertList.push({
          id: lead._id,
          severity: "critical",
          severityIcon: ErrorOutlineIcon,
          severityColor: "#EF4444",
          alertType: "Response Time Threshold Breached",
          subject: lead.client_name || lead.name || "Unknown",
          stage: lead.current_status?.key || "Lead Received",
          stageBg: "#F1F5F9",
          stageColor: "#475569",
          timeIdle: `${daysSinceCreated} days over SLA`,
          timeIdleColor: "#EF4444",
          assignedAgent: agentName,
        });
      }

      // Idle accounts - leads with no recent activity (7+ days)
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      if (
        lastUpdate < sevenDaysAgo &&
        status !== "converted" &&
        status !== "done"
      ) {
        idleAccounts++;
      }
    });

    const stats = [
      {
        label: "LEADS STUCK > 3 DAYS",
        value: stuckLeads.toString(),
        icon: AccessTimeIcon,
        bgColor: "#FFEDD5",
        iconColor: "#EA580C",
      },
      {
        label: "SLA BREACHES",
        value: slaBreaches.toString(),
        icon: ReportProblemOutlinedIcon,
        bgColor: "#FEF3C7",
        iconColor: "#F59E0B",
      },
      {
        label: "IDLE ACCOUNTS",
        value: idleAccounts.toString(),
        icon: PersonOffOutlinedIcon,
        bgColor: "#F1F5F9",
        iconColor: "#64748B",
      },
    ];

    return { alertStats: stats, alerts: alertList };
  }, [leads]);

  const filteredAlerts = alerts.filter((alert) => {
    if (filterType === "All") return true;
    if (filterType === "Critical") return alert.severity === "critical";
    if (filterType === "Warning") return alert.severity === "warning";
    return true;
  });

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleDateClick = (event) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setDateAnchorEl(null);
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setDateAnchorEl(null);
    setPage(1);
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setPage(1);
  };

  const handleRowClick = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const rowsPerPage = 10;
  const pageCount = Math.max(1, Math.ceil(filteredAlerts.length / rowsPerPage));
  const paginatedAlerts = filteredAlerts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        animation: `${fadeIn} 0.3s ease-out`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderBottom: "1px solid #E2E8F0",
          bgcolor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: "#fff",
          }}
        >
          <Box
            onClick={handleBack}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              color: "#64748B",
              fontSize: 15,
              fontWeight: 500,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1E293B",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
              Back to Dashboard
            </Typography>
          </Box>
          <Box sx={{ width: "1px", height: 24, bgcolor: "#E2E8F0" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#1E293B" }}>
            System Alerts & Monitoring
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Filter pills */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              p: 1.0,
              borderRadius: 3,
              bgcolor: "#F1F5F9",
            }}
          >
            {["All", "Critical", "Warning"].map((type) => {
              const isActive = filterType === type;
              return (
                <Box
                  key={type}
                  onClick={() => handleFilterChange(type)}
                  sx={{
                    px: 1.75,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    bgcolor: isActive ? "#fff" : "transparent",
                    color: isActive ? "#0F172A" : "#64748B",
                    boxShadow: isActive ? "0 1px 1px rgba(0,0,0,0.04)" : "none",
                    "&:hover": {
                      bgcolor: isActive ? "#fff" : "#E2E8E0",
                    },
                  }}
                >
                  {type}
                </Box>
              );
            })}
          </Box>

          {/* Date picker */}
          <Box
            onClick={handleDateClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: 38,
              px: 1.75,
              borderRadius: 3,
              border: datePickerOpen
                ? "1px solid #7C3AED"
                : "1px solid #E2E8F0",
              fontSize: 13,
              fontWeight: 600,
              color: "#0F172A",
              cursor: "pointer",
              transition: "all 0.2s ease",
              bgcolor: datePickerOpen ? "#F5F3FF" : "#fff",
              "&:hover": {
                borderColor: "#7C3AED",
                bgcolor: "#FAFAFA",
              },
            }}
          >
            {selectedDate.format("MM/DD/YYYY")}
            <CalendarTodayIcon sx={{ fontSize: 14 }} />
          </Box>

          {/* Date Picker Popover */}
          <Box sx={{ position: "relative" }}>
            <Popover
              open={datePickerOpen}
              anchorEl={dateAnchorEl}
              onClose={handleDateClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    borderRadius: 3,
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                  },
                },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={selectedDate}
                  onChange={handleDateChange}
                  sx={{
                    "& .MuiPickersDay-root.Mui-selected": {
                      bgcolor: "#7C3AED",
                      "&:hover": {
                        bgcolor: "#6D28D9",
                      },
                    },
                    "& .MuiPickersDay-root:hover": {
                      bgcolor: "#F5F3FF",
                    },
                  }}
                />
              </LocalizationProvider>
            </Popover>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: "flex", gap: 2.5, mb: 3 }}>
          {alertStats.map((stat, index) => (
            <AlertStatsCard
              key={index}
              label={stat.label}
              value={stat.value}
              icon={stat.icon}
              bgColor={stat.bgColor}
              iconColor={stat.iconColor}
              delay={index * 0.1}
            />
          ))}
        </Box>

        {/* Alerts Table */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 4,
            border: "1px solid #E2E8F0",
            overflow: "hidden",
            animation: `${fadeIn} 0.4s ease-out 0.2s both`,
          }}
        >
          {/* Top Bar */}
          <Box
            sx={{
              px: 2,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: 16, color: "#1E293B" }}
            >
              System Alerts ({filteredAlerts.length})
            </Typography>
          </Box>

          {/* Alerts List */}
          {loading ? (
            <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "#64748B" }}>Loading...</Typography>
            </Box>
          ) : filteredAlerts.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "#64748B" }}>No alerts found</Typography>
            </Box>
          ) : (
            <Box>
              {paginatedAlerts.map((alert, index) => (
                <Box
                  key={alert.id}
                  onClick={() => handleRowClick(alert.id)}
                  sx={{
                    px: 2,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderBottom: "1px solid #F1F5F9",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#F8FAFC",
                    },
                    animation: `${fadeIn} 0.3s ease-out ${index * 0.05}s both`,
                  }}
                >
                  {/* Severity Icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: alert.severityColor + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <alert.severityIcon
                      sx={{ fontSize: 18, color: alert.severityColor }}
                    />
                  </Box>

                  {/* Alert Details */}
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: alert.severityColor,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        mb: 0.25,
                      }}
                    >
                      {alert.alertType}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#1E293B",
                      }}
                    >
                      {alert.subject}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mt: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 3,
                          bgcolor: alert.stageBg,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: alert.stageColor,
                          }}
                        >
                          {alert.stage}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        • {alert.timeIdle}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                        • {alert.assignedAgent}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid #E2E8F0",
              bgcolor: "#F8FAFC",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#64748B" }}>
              Showing {paginatedAlerts.length} of {filteredAlerts.length} alerts
            </Typography>

            <Pagination
              count={pageCount}
              page={page}
              onChange={(e, val) => setPage(val)}
              size="small"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                "& .MuiPaginationItem-root": {
                  fontSize: 14,
                  fontWeight: 700,
                  height: 40,
                  minWidth: 40,
                  borderRadius: 3,
                  color: "#64748B",
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  bgcolor: "#fff",
                  border: "2px solid #E2E8F0",
                  color: "#7C3AED",
                  boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
                  "&:hover": { bgcolor: "#F8FAFC" },
                },
                "& .MuiPaginationItem-page": {
                  border: "none",
                  bgcolor: "transparent",
                  "&:hover": { bgcolor: "#F8FAFC" },
                },
                "& .MuiPaginationItem-previousNext": {
                  bgcolor: "#fff",
                  border: "2px solid #E2E8F0",
                  borderRadius: 3,
                  color: "#94A3B8",
                  "&:hover": { bgcolor: "#F8FAFC" },
                },
                "& .MuiPaginationItem-previousNext.Mui-disabled": {
                  borderColor: "#E5E7EB",
                  color: "#CBD5E1",
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SystemAlertsPage;
